import { Button, Sidebar, SidebarContent, Tabs, TabsContent, useSidebar } from '@ibsys/kit';
import { CSSProperties, FC, useCallback, useEffect, useState } from 'react';
import { cn } from '@/shared/lib';
import { RouteConfig } from '@/shared/lib/ts-router';
import { Icon, useMainLayout } from '@/shared/ui';
import { AppSidebaItems } from './AppSidebaItems';
import { PanelsBar } from '@/widgets/MainLayout/ui/PanelsBar';
import { CopilotPanel } from '@/widgets/Copilot';
import { SidebarPanels } from '@/entities/settings';
import { AppSidebarHeader } from './AppSiedbarHeader';
import { SIDEBAR_PROVIDER_ID } from '@/widgets/MainLayout/model/constants';
import { useLocation } from 'react-router-dom';

/** Минимальная и максимальная ширина панели copilot */
const COPILOT_MIN_WIDTH = 320;
const COPILOT_MAX_WIDTH = 900;

type Props = {
  menuItems: RouteConfig[];
  style?: CSSProperties;
};

export const AppSidebar: FC<Props> = (props) => {
  const { menuItems, style } = props;

  const [trigger, setTrigger] = useState<'hover' | 'click'>('click');
  const { setOpen, isMobile, openMobile, setOpenMobile } = useSidebar();
  const { sidebar } = useMainLayout();
  const { pathname } = useLocation();

  useEffect(() => {
    if (openMobile && isMobile && sidebar.activePanel === 'navigation') {
      setOpenMobile(false);
    }
  }, [pathname, isMobile]);

  const [, setDisplayWidth] = useState<number | null>(null);

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (!isMobile) {
        const startX = e.clientX;
        const startWidth = sidebar.copilotWidth;

        const handleMouseMove = (ev: MouseEvent) => {
          let delta = ev.clientX - startX;
          const newWidth = Math.max(COPILOT_MIN_WIDTH, Math.min(COPILOT_MAX_WIDTH, startWidth + delta));
          setDisplayWidth(newWidth);
          requestAnimationFrame(() => {
            document.getElementById(SIDEBAR_PROVIDER_ID)?.style.setProperty('--sidebar-width-temp', `${newWidth}px`);
          });
        };

        const handleMouseUp = () => {
          setDisplayWidth((width) => {
            width && sidebar.setCopilotWidth(width);
            return null;
          });
          document.getElementById(SIDEBAR_PROVIDER_ID)?.style.removeProperty('--sidebar-width-temp');
          window.removeEventListener('mousemove', handleMouseMove);
          window.removeEventListener('mouseup', handleMouseUp);
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
      }
    },
    [sidebar],
  );

  const handlerTriggerChange = () => {
    setTrigger((prev) => (prev === 'click' ? 'hover' : 'click'));
  };

  const handleTriggerClick = () => {
    setOpen(trigger === 'hover');
    handlerTriggerChange();
  };

  return (
    <Sidebar
      classNames={{
        wrapper: '',
        inner: 'gap-0',
      }}
      className={cn(!isMobile && 'z-20')}
      style={style}
      collapsible={'icon'}
      variant={'sidebar'}
      onMouseOver={trigger === 'hover' ? () => setOpen(true) : undefined}
      onMouseLeave={trigger === 'hover' ? () => setOpen(false) : undefined}
    >
      <Tabs
        className={'flex-1 flex flex-row gap-0 h-full'}
        value={sidebar.activePanel}
        orientation={'vertical'}
        onValueChange={(v) => sidebar.setActivePanel(v as SidebarPanels)}
      >
        <PanelsBar className={'px-2 h-full'} />
        <SidebarContent className={cn('transition-[translate] duration-200')}>
          <TabsContent value={'navigation'} className={'flex-1 flex flex-col'}>
            <AppSidebarHeader className={'sticky top-0 z-1'} />
            <AppSidebaItems menuItems={menuItems} className={'px-2'} />
          </TabsContent>
          <TabsContent value={'copilot'} className={'flex-1 flex flex-col h-full relative'}>
            <AppSidebarHeader className={'sticky top-0 z-1'} />
            <CopilotPanel />
            <div
              role={'separator'}
              tabIndex={-1}
              className={'absolute right-0 top-0 h-full w-1 cursor-col-resize z-30 hover:bg-border transition-colors'}
              onMouseDown={handleResizeStart}
            />
          </TabsContent>
        </SidebarContent>
      </Tabs>
      {!isMobile && (
        <Button
          icon={<Icon type={trigger === 'hover' ? 'unpin' : 'pin'} />}
          className={cn(
            'absolute right-0 translate-x-1/2 z-20 size-7.5 top-3 border-border border bg-secondary text-muted-foreground transition-all',
            trigger === 'hover' && 'bg-accent',
          )}
          onClick={handleTriggerClick}
          onMouseOver={(e) => trigger === 'hover' && e.stopPropagation()}
          variant={'secondary'}
          size={'icon'}
        />
      )}
    </Sidebar>
  );
};
